import React, { PropsWithChildren } from 'react'
import AppBar from '../../components/AppBar'
import { Helmet } from 'react-helmet'
import styles from './styles.module.scss'
import classNames from 'classnames'
import { RainbowText } from '../../components/RainbowText'
import ss1 from './assets/ss1.png'
import ss2 from './assets/ss2.png'
import ss3 from './assets/ss3.png'
import ss4 from './assets/ss4.png'
import ss5 from './assets/ss5.png'
import { Screenshot } from '../../components/Screenshot'
import Fade from 'react-reveal/Fade'
import { ifElse } from '../../utilities'
import { AppFooter } from '../../components/AppFooter'
import List from '../../components/List'
import DownloadAndDiscordButtons from '../../components/DownloadAndDiscordButtons'

const DracoBot: React.FC<PropsWithChildren<{ reverse?: boolean; light?: boolean }>> = ({ reverse = true, light = true }) => {
  return <RainbowText light={light} reverse={reverse} text='Draco.Bot' />
}

type HomepageHeaderProps = {
  title: string
  subtitle: string
  reverse?: boolean
}

const HomepageHeader: React.FC<HomepageHeaderProps> = ({ title, subtitle, reverse }) => {
  return (
    <h1>
      <RainbowText text={title} reverse={reverse} />
      <small>{subtitle}</small>
    </h1>
  )
}

type HomepageSectionProps = PropsWithChildren<{
  title: string
  className?: string
  subtitle: string
  screenshot?: string
  reverse?: boolean
  hideMobileScreenshot?: boolean
}>

const HomepageSection: React.FC<HomepageSectionProps> = ({ title, subtitle, screenshot, reverse, hideMobileScreenshot, className, children }) => {
  const ss = !screenshot ? null : (
    <Fade key='ss' duration={1000}>
      <Screenshot className={styles.screenshot} src={[screenshot]} glowAmount='small' reverse={!reverse} />
    </Fade>
  )

  const content = (
    <Fade key='content' duration={300} distance='100px' right={!reverse} left={reverse}>
      <div className={styles.textContent}>
        <HomepageHeader {...{ title, subtitle, reverse }} />
        <div className={classNames('display-6', styles.subtitle)}>{children}</div>
      </div>
    </Fade>
  )

  return (
    <Fade duration={1000}>
      <div className={classNames(styles.container, className, 'container')}>
        <div className={classNames(styles.content, ss ? styles.twoColumn : styles.noScreenshot, hideMobileScreenshot && styles.hideMobileScreenshot)}>
          {ifElse(!reverse, [ss, content], [content, ss])}
        </div>
      </div>
    </Fade>
  )
}

function ChatbotHome() {
  return (
    <div>
      <Helmet>
        <meta charSet='utf-8' />
        <title>Home | Draco.Bot</title>
      </Helmet>

      <AppBar />

      <div className={classNames(styles.container, styles.twoColumnContainer)}>
        <div className='container'>
          <div className={styles.content}>
            <div className={styles.textcontent}>
              <HomepageHeader title='Take Control of Your Stream' subtitle='with automation that actually keeps up' reverse />
              <div className={classNames('display-6', styles.subtitle)}>
                <p>
                  <DracoBot /> brings chat, Twitch events, OBS, audio, and stream automation into one desktop app, so your stream can react together without a mess of separate tools.
                </p>
                <List
                  className={styles.list}
                  more={false}
                  items={[
                    'Runs locally on your PC for fast, dependable control',
                    'Automates chat, channel points, and Twitch events',
                    'Controls OBS, audio, and stream actions from one place',
                    'Supports voice commands for hands-free moments',
                  ]}
                />
              </div>
            </div>

            <div className={styles.buttons}>
              <DownloadAndDiscordButtons />
            </div>

            <div className={styles.screenshots}>
              <Screenshot src={[ss1, ss2, ss3, ss4, ss5]} />
            </div>
          </div>
        </div>
      </div>

      <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 50' className={styles.decoration}>
        <path fillOpacity='1' d='M0,50 C100,20 550,0 800,0 L 0,0' />
      </svg>

      <HomepageSection title='Stop' subtitle='managing your stream the hard way' screenshot={ss1}>
        <p>Streaming often means piecing together bots, browser tools, plugins, and workarounds that all react to the same moments in different ways.</p>
        <p>That can work for a while, until something lags, misses an event, or stops cooperating right when you need it.</p>
        <p>
          <b>
            <DracoBot /> was built to simplify that.
          </b>{' '}
          It gives your stream one place to handle the reactions, responses, and automation that would otherwise be scattered across multiple tools.
        </p>
      </HomepageSection>

      <HomepageSection title='One Bot for' subtitle='chat, OBS, and more' screenshot={ss2} reverse>
        <p>
          <DracoBot /> helps your stream feel more connected by bringing chat, Twitch events, OBS, audio, and other automations into the same system.
        </p>
        <List
          className={styles.list}
          more={false}
          items={[
            'React to chat, channel points, raids, subs, ads, and other Twitch events',
            'Send messages, announcements, shoutouts, and stream updates automatically',
            'Control OBS scenes, sources, filters, media, and audio directly',
            'Run timers, counters, action chains, and overlay-friendly output',
            'Trigger music, sound effects, text-to-speech, and VTube Studio actions',
          ]}
        />
      </HomepageSection>

      <HomepageSection title='Built' subtitle='for live streams' screenshot={ss3} hideMobileScreenshot>
        <p>
          Live streaming is not the place for tools that need babysitting. <DracoBot /> is built to stay responsive when timing matters and your setup needs to keep up.
        </p>
        <List
          className={styles.list}
          more={false}
          items={[
            'Runs locally instead of relying on browser-based tools or cloud delays',
            'No browser tabs to keep refreshing',
            'Action queuing helps prevent overlaps and conflicts',
            'Automatic reconnects for Twitch and OBS',
            'Configuration backups and recovery',
          ]}
        />
      </HomepageSection>

      <HomepageSection title='Keep your Stream Interactive' subtitle='without doing everything by hand' screenshot={ss4} reverse>
        <p>
          Set up the moments you want once, then let <DracoBot /> handle them live while you focus on the stream itself.
        </p>
        <List
          className={styles.list}
          more={false}
          items={[
            'React to viewer interactions, chat activity, and channel point rewards',
            'Trigger sounds, overlays, and OBS changes from live events',
            'Create shoutouts, timed messages, and recurring stream moments',
            'Run raffles, counters, timers, and other interactive features',
            'Give regulars, VIPs, or subs access to special interactions',
          ]}
        />
      </HomepageSection>

      <HomepageSection title='Automation' subtitle='that works when it matters' screenshot={ss5} hideMobileScreenshot>
        <p>
          Whether your setup is simple or more elaborate, <DracoBot /> helps your stream feel smoother, more responsive, and easier to run live.
        </p>
        <List
          className={styles.list}
          more={false}
          items={[
            'A channel point reward that plays a sound, switches an OBS scene, and updates an overlay',
            'A countdown that starts, stops, or resets as your stream changes',
            'Music and sound effects triggered as part of larger action chains',
            'VTuber model actions triggered by chat commands or stream events',
            'Hands-free voice commands for moments when your hands are busy',
          ]}
        />
      </HomepageSection>

      <HomepageSection title='Spend Less Time Managing' subtitle='and more time actually streaming' className={styles.noAlt}>
        <p>
          <DracoBot /> is currently in public beta and actively improved based on real streamer feedback.
        </p>
        <p>If you want a stream setup that feels smoother, more responsive, and easier to run live, you&apos;re in the right place.</p>
        <div className={styles.buttons}>
          <DownloadAndDiscordButtons />
        </div>
      </HomepageSection>

      <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 50' className={classNames(styles.decoration, styles.flip)}>
        <path fillOpacity='1' d='M0,50 C100,20 550,0 800,0 L 0,0' />
      </svg>

      <div className={styles.footer}>
        <AppFooter />
      </div>
    </div>
  )
}

export default ChatbotHome
